import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { Provider } from "react-redux";
import { AuthProvider, AuthProviderProps } from "react-oidc-context";
import { userManager } from "./lib/oidc";
import { store } from "./store/store";
import { setupInterceptors } from "./lib/api";
import { installStaleChunkRecovery } from "./lib/staleChunkRecovery";
import "./store/listeners";
import { Toaster } from "sonner";

// Recover automatically when a deploy invalidates lazy-loaded chunks
// (otherwise navigation gets stuck on the loading spinner until a hard refresh).
installStaleChunkRecovery();

// All OIDC settings live in ./lib/oidc so non-React code (and the silent-renew
// recovery) can reach the same UserManager instance.
const oidcConfig: AuthProviderProps = {
  userManager,

  // Clean up the URL after successful login
  onSigninCallback: (_user: any) => {
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname.replace("/callback", "/"),
    );
  },
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep cached pages warm long enough that quick back-and-forth
      // navigation doesn't refetch everything from scratch.
      gcTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 60 * 1000, // 1 minute
      // Prevent excessive refetches
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

setupInterceptors(store);

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <AuthProvider {...oidcConfig}>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <Toaster
          position="bottom-right"
          theme="dark"
          duration={5000}
          richColors
          toastOptions={{
            classNames: {
              toast: "p-4 border border-neutral-700 bg-neutral-800 text-white",
              title: "text-sm font-medium",
              description: "text-sm text-neutral-400",
            },
          }}
        />
        <App />
      </QueryClientProvider>
    </Provider>
  </AuthProvider>,
);
